/**
 * AuthServiceWrapper - Gradual migration between old and new auth services
 * Uses feature flags to control which service is active
 */

import { isFeatureEnabled, logFeatureUsage } from '../config/featureFlags';
import authService from './AuthService';
import SecureAuthService from './SecureAuthService';

class AuthServiceWrapper {
  constructor() {
    this.oldAuthService = authService;
    this.newAuthService = new SecureAuthService();
    
    // Log which service is being used
    if (isFeatureEnabled('USE_NEW_AUTH_SERVICE')) {
      logFeatureUsage('USE_NEW_AUTH_SERVICE', 'initialized');
      console.log('🔐 Using NEW SecureAuthService');
    } else {
      console.log('🔐 Using LEGACY AuthService');
    }
  }

  // Get the active auth service based on feature flags
  getActiveService() {
    return isFeatureEnabled('USE_NEW_AUTH_SERVICE') 
      ? this.newAuthService 
      : this.oldAuthService;
  }

  // Proxy all auth methods to the active service
  async register(userData) {
    const service = this.getActiveService();
    logFeatureUsage('AUTH_REGISTER', `via ${service.constructor.name}`);
    return await service.register(userData);
  }

  async login(email, password) {
    const service = this.getActiveService();
    logFeatureUsage('AUTH_LOGIN', `via ${service.constructor.name}`);
    return await service.login(email, password);
  }

  async logout() {
    const service = this.getActiveService();
    logFeatureUsage('AUTH_LOGOUT', `via ${service.constructor.name}`);
    return await service.logout();
  }

  async resetPassword(email) {
    const service = this.getActiveService();
    return await service.resetPassword(email);
  }

  async updateProfile(updates) {
    const service = this.getActiveService();
    return await service.updateProfile(updates);
  }

  async updateScenePoints(pointsToAdd) {
    const service = this.getActiveService();
    if (service.updateScenePoints) {
      return await service.updateScenePoints(pointsToAdd);
    } else {
      // Fallback implementation using ApiService
      try {
        const { supabase } = await import('../config/supabase');
        const currentUser = this.currentUser;
        
        if (!currentUser) {
          return { success: false, error: 'No user logged in' };
        }

        const { data, error } = await supabase
          .from('users')
          .update({ 
            scene_points: supabase.raw(`scene_points + ${pointsToAdd}`)
          })
          .eq('id', currentUser.id)
          .select('scene_points')
          .single();

        if (error) throw error;

        // Update the current user object
        if (currentUser) {
          currentUser.scene_points = data.scene_points;
        }

        return { 
          success: true, 
          newTotal: data.scene_points,
          pointsAdded: pointsToAdd
        };
      } catch (error) {
        console.error('Error updating scene points:', error);
        return { success: false, error: error.message };
      }
    }
  }

  // Getters that work with both services
  get currentUser() {
    return this.getActiveService().currentUser;
  }

  get isLoggedIn() {
    return this.getActiveService().isLoggedIn;
  }

  get isLoading() {
    return this.getActiveService().isLoading;
  }

  get isAdmin() {
    return this.getActiveService().isAdmin;
  }

  get isAdvertiser() {
    return this.getActiveService().isAdvertiser;
  }

  // Event listeners
  addListener(callback) {
    return this.getActiveService().addListener(callback);
  }

  removeListener(callback) {
    return this.getActiveService().removeListener(callback);
  }

  // Migration utilities
  async migrateUser() {
    if (!isFeatureEnabled('USE_NEW_AUTH_SERVICE')) {
      console.log('⚠️ Migration skipped - new auth service not enabled');
      return { success: false, reason: 'Feature not enabled' };
    }

    const oldUser = this.oldAuthService.currentUser;
    if (!oldUser) {
      return { success: false, reason: 'No user to migrate' };
    }

    try {
      // Create user profile in new system
      const result = await this.newAuthService.createUserProfile(oldUser);
      logFeatureUsage('USER_MIGRATION', result.success ? 'success' : 'failed');
      return result;
    } catch (error) {
      console.error('Migration error:', error);
      return { success: false, error: error.message };
    }
  }

  // Debug utilities
  getServiceStatus() {
    return {
      activeService: this.getActiveService().constructor.name,
      featureFlags: {
        useNewAuth: isFeatureEnabled('USE_NEW_AUTH_SERVICE'),
        enableDualAuth: isFeatureEnabled('ENABLE_DUAL_AUTH'),
        allowFallback: isFeatureEnabled('ALLOW_FALLBACK_AUTH')
      },
      currentUser: this.currentUser?.email || 'None'
    };
  }
}

// Export singleton instance
const authServiceWrapper = new AuthServiceWrapper();
export default authServiceWrapper;
